import React, {
  useState,
} from 'react';
import {
  useFormContext,
} from 'react-hook-form';
import isEmpty from 'lodash/isEmpty';
import {
  styled,
} from '@mui/material/styles';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Tooltip, {
  TooltipProps,
  tooltipClasses,
} from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import {
  useMixpanelTrack,
} from 'core/hooks';
import {
  AIIcon,
} from 'core/icons';
import {
  ServiceType,
  VisitNoteServiceCode,
} from 'views/EMR/components/ServiceLookup/type';
import SuggestedCodesContent from 'views/EMR/components/ServiceLookup/SuggestedCodesList/SuggestedCodesContent';
import clsx from 'clsx';

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
    maxWidth: 336,
  },
}));

export type SuggestedCodeProps = {
  type: ServiceType;
  onChange: (value: VisitNoteServiceCode[]) => void;
  saveVisitNote?: () => Promise<void>;
  disabled?: boolean;
};

export default function SuggestedCodesList({
  saveVisitNote,
  type,
  onChange,
  disabled,
}: SuggestedCodeProps) {
  const { watch } = useFormContext();
  const mixpanelTrack = useMixpanelTrack();
  const isAssessmentCode = type === 'ICDCode';
  const [
    additionalHPI,
    physicalExam,
    plan,
  ] = watch([
    'additionalHPI',
    'physicalExam',
    'plan',
  ]);
  const suggestionParam = isAssessmentCode
    ? (additionalHPI || physicalExam)
    : plan;
  const [open, setOpen] = useState<boolean>(false);

  const handleTooltipOpen = () => {
    setOpen(true);
    mixpanelTrack('openedCodeSuggestionMenu', { type });
  };

  const handleTooltipClose = () => {
    setOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <span className={clsx({ '!cursor-not-allowed': disabled })}>
        <LightTooltip
          classes={{ popper: 'light-top' }}
          onClose={handleTooltipClose}
          open={open}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          placement="top-start"
          title={!isEmpty(suggestionParam) ? (
            <SuggestedCodesContent
              type={type}
              onChange={onChange}
              saveVisitNote={saveVisitNote}
            />
          ) : (
            <span>
              Enter
              {' '}
              {isAssessmentCode ? 'physical exam or additional HPI' : 'plan'}
              {' '}
              to get
              {' '}
              {isAssessmentCode ? 'ICD' : 'CPT'}
              {' '}
              code suggestions
            </span>
          )}
        >
          <IconButton
            tabIndex={-1}
            color="info"
            onClick={handleTooltipOpen}
            disabled={disabled}
          >
            <AIIcon className="!text-base text-gray-500" />
          </IconButton>
        </LightTooltip>
      </span>
    </ClickAwayListener>
  );
}
